import { Flex, Stack, TextInput, Textarea } from '@mantine/core';
import { FC, useEffect, useState } from 'react';
import { useCreatePageTemplate, useUpdatePageTemplate } from '../../../../api/pages-client/pages-client.hooks';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import { DashboardModalStyled } from '../dashboard-modal';

interface DashboardTemplateModalProps {
  opened: boolean;
  onClose: () => void;
  initialData?: {
    id?: string;
    name: string;
    description: string;
    page_id?: string;
  };
  isEditing?: boolean;
  onSuccess?: () => void;
  onError?: () => void;
}

export const DashboardTemplateModal: FC<DashboardTemplateModalProps> = ({
  opened,
  onClose,
  initialData = { name: '', description: '' },
  isEditing = false,
  onSuccess,
  onError,
}) => {
  const [name, setName] = useState(initialData.name);
  const [description, setDescription] = useState(initialData.description);

  useEffect(() => {
    setName(initialData.name);
    setDescription(initialData.description);
  }, [initialData]);

  const createPageTemplateMutation = useCreatePageTemplate();
  const updatePageTemplateMutation = useUpdatePageTemplate();

  const handleSave = () => {
    if (isEditing && initialData.id) {
      updatePageTemplateMutation.mutate(
        {
          id: initialData.id,
          payload: {
            name,
            description,
          },
        },
        {
          onSuccess: () => {
            handleClose();
            onSuccess?.();
          },
          onError: () => {
            onError?.();
          },
        },
      );
    } else if (initialData.page_id) {
      createPageTemplateMutation.mutate(
        {
          page_id: initialData.page_id,
          payload: {
            name,
            description,
          },
        },
        {
          onSuccess: () => {
            handleClose();
            onSuccess?.();
          },
          onError: () => {
            onError?.();
          },
        },
      );
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    onClose();
  };

  const isLoading = createPageTemplateMutation.isPending || updatePageTemplateMutation.isPending;
  const error = createPageTemplateMutation.error || updatePageTemplateMutation.error;

  return (
    <DashboardModalStyled
      opened={opened}
      onClose={handleClose}
      title={
        <Flex align="center" gap="sm">
          <Text weight="bold" size="large">
            {isEditing ? 'Edit Dashboard Template' : 'Create Dashboard Template'}
          </Text>
        </Flex>
      }
      size="lg"
      centered
    >
      <Stack gap="md">
        <TextInput
          label="Template Name"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          required
          placeholder="Dashboard Template Name"
        />
        <Textarea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          minRows={2}
          placeholder="Template for daily dashboard views"
        />
        {error && (
          <Text color="red" size="small">
            An error occurred: {error.message || 'Unknown error'}
          </Text>
        )}
      </Stack>
      <Flex justify="flex-end" gap="sm" mt="xl">
        <Button radius="xl" variant="outline" onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button radius="xl" variant="secondary" onClick={handleSave} disabled={!name || isLoading}>
          {isEditing ? 'Save Changes' : 'Create Template'}{' '}
          <Icon
            style={{ marginLeft: '8px' }}
            name={isEditing ? 'check_circle_outline' : 'add_circle_outline'}
            size={16}
            color="white"
          />
        </Button>
      </Flex>
    </DashboardModalStyled>
  );
};
